import type { EditAssetWorkflowOptions } from '../../types';
import { isNumberRegex } from './createPackaging';
export const editPackaging: EditAssetWorkflowOptions = {
  workflowKey: 'editPackagingWorkflow',
  workflowType: 'editAssetWorkflow',
  assetType: 'packaging',
  workflowIndex: 2,
  workflowName: 'Edit Packaging ',
  workflowDescription: `Tell us what kind of packages are available at your FCs`,
  workflowAssets: [],
  workflowStateTransformFunction: (assetPayload: any) => {
    const {
      description,
      partnerProvidedId,
      packagingMaterial,
      weightUnit,
      packagingWeight,
      packagingWeightkg,
      maxContentWeight,
      maxContentWeightkg,
      packagingSizeName,
      name,
      linearDimensions,
      linearDimensionscm,
      unsupportedProductDetails,
    } = assetPayload;
    return {
      active: true,
      archived: false,
      description,
      linearDimensions: {
        ...(linearDimensions.linearUnit === 'cm' ? linearDimensionscm : linearDimensions),
        linearUnit: linearDimensions.linearUnit,
      },
      maxContentWeight: {
        weight: packagingWeight.weightUnit === 'kg' ? maxContentWeightkg : maxContentWeight,
        weightUnit: weightUnit,
      },
      name,
      partnerProvidedId,
      packagingMaterial,
      packagingSizeName,
      packagingWeight: {
        weight: packagingWeight.weightUnit === 'kg' ? packagingWeightkg : packagingWeight,
        weightUnit: weightUnit,
      },
      unsupportedProductDetails: unsupportedProductDetails ? unsupportedProductDetails : [],
    };
  },
  workflowStepOptions: [
    {
      workflowStepKey: 'linearUnit',
      workflowStepNavigationName: 'Linear Unit',
      workflowComponent: {
        workflowComponentType: 'singleSelect',
        prompt: 'Which unit do you use to express dimensions?',
        description: ``,
        help: '',
        options: [
          { optionText: 'cm (Centimetres)', optionValue: 'cm' },
          { optionText: 'in (Inches)', optionValue: 'in' },
        ],
      },
      workflowStateToAssetMapping: {
        mappingType: 'nestedProperty',
        parent: 'linearDimensions',
        property: 'linearUnit',
      },
    },
    {
      workflowStepKey: 'weightUnit',
      workflowStepNavigationName: 'Weight Unit',
      workflowComponent: {
        workflowComponentType: 'singleSelect',
        prompt: 'Which unit do you use to express weight restrictions?',
        description: ``,
        help: '',
        options: [
          { optionText: 'kg (Kilograms)', optionValue: 'kg' },
          { optionText: 'lb (Pounds)', optionValue: 'lb' },
        ],
      },
      workflowStateToAssetMapping: {
        mappingType: 'property',
        property: 'weightUnit',
      },
      assetToWorkflowStateMapping: {
        computeFn: (pckgWeight) => pckgWeight.weightUnit,
        assetProperty: 'packagingWeight',
      },
    },
    {
      workflowStepKey: 'length',
      workflowStepNavigationName: 'Packaging Length',
      excludeFromNavigation: false,
      refreshAssetsOnStepCompletion: true,
      workflowComponent: {
        validationRegex: isNumberRegex,
        validationMessage: 'Length must be a number',
        workflowComponentType: 'textEntry',
        prompt: 'What is the length of your packaging (in inches)?',
        description: 'Enter a length for your packaging',
        help: '',
      },
      workflowStepConditionalMatcher: [
        {
          workflowStepKey: 'linearUnit',
          schema: { type: 'string', const: 'in' },
        },
      ],
      workflowStateToAssetMapping: {
        mappingType: 'nestedProperty',
        parent: 'linearDimensions',
        property: 'length',
      },
    },
    {
      workflowStepKey: 'lengthcm',
      workflowStepNavigationName: 'Packaging Length',
      excludeFromNavigation: false,
      refreshAssetsOnStepCompletion: true,
      workflowComponent: {
        validationRegex: isNumberRegex,
        validationMessage: 'Length must be a number',
        workflowComponentType: 'textEntry',
        prompt: 'What is the length of your packaging (in centimeters)?',
        description: 'Enter a length for your packaging',
        help: '',
      },
      workflowStepConditionalMatcher: [
        {
          workflowStepKey: 'linearUnit',
          schema: { type: 'string', const: 'cm' },
        },
      ],
      assetToWorkflowStateMapping: {
        assetProperty: 'linearDimensions',
        computeFn: (dim) => dim.length,
      },
      workflowStateToAssetMapping: {
        mappingType: 'nestedProperty',
        parent: 'linearDimensionscm',
        property: 'length',
      },
    },
    {
      workflowStepKey: 'width',
      workflowStepNavigationName: 'Packaging Width',
      excludeFromNavigation: false,
      refreshAssetsOnStepCompletion: true,
      workflowComponent: {
        validationRegex: isNumberRegex,
        validationMessage: 'Width must be a number',
        workflowComponentType: 'textEntry',
        prompt: 'What is the width of your packaging (in inches)?',
        description: 'Enter a width for your packaging',
        help: '',
      },
      workflowStepConditionalMatcher: [
        {
          workflowStepKey: 'linearUnit',
          schema: { type: 'string', const: 'in' },
        },
      ],
      workflowStateToAssetMapping: {
        mappingType: 'nestedProperty',
        parent: 'linearDimensions',
        property: 'width',
      },
    },
    {
      workflowStepKey: 'widthcm',
      workflowStepNavigationName: 'Packaging Width',
      excludeFromNavigation: false,
      refreshAssetsOnStepCompletion: true,
      workflowComponent: {
        validationRegex: isNumberRegex,
        validationMessage: 'Width must be a number',
        workflowComponentType: 'textEntry',
        prompt: 'What is the width of your packaging (in centimeters)?',
        description: 'Enter a width for your packaging',
        help: '',
      },
      workflowStepConditionalMatcher: [
        {
          workflowStepKey: 'linearUnit',
          schema: { type: 'string', const: 'cm' },
        },
      ],
      assetToWorkflowStateMapping: {
        assetProperty: 'linearDimensions',
        computeFn: (dim) => dim.width,
      },
      workflowStateToAssetMapping: {
        mappingType: 'nestedProperty',
        parent: 'linearDimensionscm',
        property: 'width',
      },
    },
    {
      workflowStepKey: 'height',
      workflowStepNavigationName: 'Packaging Height',
      excludeFromNavigation: false,
      refreshAssetsOnStepCompletion: true,
      workflowComponent: {
        validationRegex: isNumberRegex,
        validationMessage: 'Height must be a number',
        workflowComponentType: 'textEntry',
        prompt: 'What is the height of your packaging (in inches)?',
        description: 'Enter a height for your packaging',
        help: '',
      },
      workflowStepConditionalMatcher: [
        {
          workflowStepKey: 'linearUnit',
          schema: { type: 'string', const: 'in' },
        },
      ],
      workflowStateToAssetMapping: {
        mappingType: 'nestedProperty',
        parent: 'linearDimensions',
        property: 'height',
      },
    },

    {
      workflowStepKey: 'heightcm',
      workflowStepNavigationName: 'Packaging Height',
      excludeFromNavigation: false,
      refreshAssetsOnStepCompletion: true,
      workflowComponent: {
        validationRegex: isNumberRegex,
        validationMessage: 'Height must be a number',
        workflowComponentType: 'textEntry',
        prompt: 'What is the height of your packaging (in centimeters)?',
        description: 'Enter a height for your packaging',
        help: '',
      },
      workflowStepConditionalMatcher: [
        {
          workflowStepKey: 'linearUnit',
          schema: { type: 'string', const: 'cm' },
        },
      ],
      assetToWorkflowStateMapping: {
        assetProperty: 'linearDimensions',
        computeFn: (dim) => dim.height,
      },
      workflowStateToAssetMapping: {
        mappingType: 'nestedProperty',
        parent: 'linearDimensionscm',
        property: 'height',
      },
    },

    {
      workflowStepKey: 'weight',
      workflowStepNavigationName: 'Packaging Weight',
      excludeFromNavigation: false,
      refreshAssetsOnStepCompletion: true,
      workflowComponent: {
        validationRegex: isNumberRegex,
        validationMessage: 'Packaging weight must be a number',
        workflowComponentType: 'textEntry',
        prompt: 'What is the weight of your packaging (in pounds)?',
        description: '',
        help: '',
      },
      workflowStateToAssetMapping: {
        mappingType: 'property',
        property: 'packagingWeight',
      },
      assetToWorkflowStateMapping: {
        assetProperty: 'packagingWeight',
        computeFn: (weight) => weight.weight,
      },
      workflowStepConditionalMatcher: [
        {
          workflowStepKey: 'weightUnit',
          schema: { type: 'string', const: 'lb' },
        },
      ],
    },
    {
      workflowStepKey: 'weightkg',
      workflowStepNavigationName: 'Packaging Weight',
      excludeFromNavigation: false,
      refreshAssetsOnStepCompletion: true,
      workflowComponent: {
        validationRegex: isNumberRegex,
        validationMessage: 'Packaging weight must be a number',
        workflowComponentType: 'textEntry',
        prompt: 'What is the weight of your packaging (in kilograms)?',
        description: '',
        help: '',
      },
      workflowStateToAssetMapping: {
        mappingType: 'property',
        property: 'packagingWeightkg',
      },
      assetToWorkflowStateMapping: {
        assetProperty: 'packagingWeight',
        computeFn: (weight) => weight.weight,
      },
      workflowStepConditionalMatcher: [
        {
          workflowStepKey: 'weightUnit',
          schema: { type: 'string', const: 'kg' },
        },
      ],
    },
    {
      workflowStepKey: 'maxContentWeight',
      workflowStepNavigationName: 'Max Content Weight',
      excludeFromNavigation: false,
      refreshAssetsOnStepCompletion: true,
      workflowComponent: {
        validationRegex: isNumberRegex,
        validationMessage: 'Max content weight must be a number',
        workflowComponentType: 'textEntry',
        prompt: 'What is the max content weight of your packaging (in pounds)?',
        description: 'Enter the maximum weight that this package allow',
        help: '',
      },
      workflowStepConditionalMatcher: [
        {
          workflowStepKey: 'weightUnit',
          schema: { type: 'string', const: 'lb' },
        },
      ],
      assetToWorkflowStateMapping: {
        assetProperty: 'maxContentWeight',
        computeFn: (weight) => weight.weight,
      },
      workflowStateToAssetMapping: {
        mappingType: 'property',
        property: 'maxContentWeight',
      },
    },
    {
      workflowStepKey: 'maxContentWeightkg',
      workflowStepNavigationName: 'Max Content Weight',
      excludeFromNavigation: false,
      refreshAssetsOnStepCompletion: true,
      workflowComponent: {
        validationRegex: isNumberRegex,
        validationMessage: 'Max content weight must be a number',
        workflowComponentType: 'textEntry',
        prompt: 'What is the max content weight of your packaging (in kilograms)?',
        description: 'Enter the maximum weight that this package allows',
        help: '',
      },
      workflowStepConditionalMatcher: [
        {
          workflowStepKey: 'weightUnit',
          schema: { type: 'string', const: 'kg' },
        },
      ],
      assetToWorkflowStateMapping: {
        assetProperty: 'maxContentWeight',
        computeFn: (weight) => weight.weight,
      },
      workflowStateToAssetMapping: {
        mappingType: 'property',
        property: 'maxContentWeightkg',
      },
    },
    {
      workflowStepKey: 'packagingMaterial',
      workflowStepNavigationName: 'Packaging Material',
      workflowComponent: {
        workflowComponentType: 'singleSelect',
        prompt: 'Which of these best describes your packaging?',
        description: '',
        help: '',
        options: [
          { optionText: 'Box', optionValue: 'box' },
          { optionText: 'Envelope', optionValue: 'envelope' },
          { optionText: 'Flat Pack', optionValue: 'flat_pack' },
        ],
      },
      workflowStateToAssetMapping: {
        mappingType: 'property',
        property: 'packagingMaterial',
      },
    },
    {
      workflowStepKey: 'unsupportedProductDetails',
      workflowStepNavigationName: 'Unsupported Product Details',
      optional: true,
      workflowComponent: {
        workflowComponentType: 'multipleSelect',
        prompt: 'Which product types cannot be packed into this packaging?',
        description: 'If there are restrictions on what kind of items this packaging can hold, specify them here',
        help: '',
        defaultValue: [],
        options: [
          { optionText: 'ORMD', optionValue: 'ormd' },
          { optionText: 'LIO', optionValue: 'lio' },
          { optionText: 'Fragile', optionValue: 'fragile' },
        ],
      },
      workflowStateToAssetMapping: {
        mappingType: 'property',
        property: 'unsupportedProductDetails',
      },
    },
    {
      workflowStepKey: 'name',
      workflowStepNavigationName: 'Name',
      workflowComponent: {
        workflowComponentType: 'assetNameEntry',
        prompt: 'What will you call this package?',
        description: 'How will you refer to this package?',
        help: '',
      },
      workflowStateToAssetMapping: {
        mappingType: 'properties',
        properties: ['name', 'packagingSizeName'],
      },
    },
    {
      workflowStepKey: 'partnerProvidedId',
      workflowStepNavigationName: 'Packaging ID Alias',
      optional: true,
      workflowComponent: {
        workflowComponentType: 'textEntry',
        prompt: 'Would you like to give this package a custom ID?',
        description:
          'You may optionally define this ID, and it can be used in lieu of the Shipium generated Packaging ID.',
        help: '',
      },
      workflowStateToAssetMapping: {
        mappingType: 'property',
        property: 'partnerProvidedId',
      },
    },
    {
      workflowStepKey: 'description',
      workflowStepNavigationName: 'Description',
      optional: true,
      workflowComponent: {
        workflowComponentType: 'textEntry',
        rows: 4,
        defaultValue: '',
        prompt: 'Enter a description (optional):',
        description: '',
        help: '',
      },
      workflowStateToAssetMapping: {
        mappingType: 'property',
        property: 'description',
      },
    },
    // {
    //   workflowStepKey: 'tenantSelect',
    //   workflowStepNavigationName: 'Tenant Select',
    //   workflowComponent: {
    //     assetType: 'tenant',
    //     workflowComponentType: 'assetSetSelect',
    //     prompt: 'Does this packaging belong to a specific tenant?',
    //     description: '',
    //     help: 'Specifying a tenant will allow you to more easily filter you various configuration pieces',
    //     optional: true
    //   },
    //   calculatedComponentProps: {
    //     assetId: {
    //       type: 'asset',
    //       property: 'packagingId'
    //     }
    //   },
    //   workflowStepConditionalMatcher: [{
    //     assetType: 'tenant',
    //     schema: { type: 'array', minItems: 1 }
    //   }],
    //   workflowStateToAssetMapping: {
    //     mappingType: 'link',
    //     linkAssetType: 'tenant',
    //     linkType: 'Tenant'
    //   }
    // },
    {
      workflowStepKey: 'summary',
      workflowStepNavigationName: 'Configuration Summary',
      workflowComponent: {
        workflowComponentType: 'workflowSummary',
        title: 'Update Packaging Summary',
        description: 'The following packaging will be updated.  Please review your selections.',
        help: '',
      },
      workflowStateToAssetMapping: {
        mappingType: 'ignore',
      },
    },
  ],
};
